import { useState, useEffect, FormEvent, useRef } from 'react';
import {
  getDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  uploadFile,
  Document,
  DocCategory,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import './Documents.css';
import { overlayClose } from '../utils/overlayClose';

const CATEGORY_LABELS: Record<DocCategory, string> = {
  general: 'General',
  safeguarding: 'Safeguarding',
  policies: 'Policies',
  minutes: 'Minutes',
  forms: 'Forms',
  other: 'Other',
};

const CATEGORIES: DocCategory[] = ['general', 'safeguarding', 'policies', 'minutes', 'forms', 'other'];

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(fileName?: string): string {
  if (!fileName) return '📄';
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return '📕';
  if (ext === 'doc' || ext === 'docx') return '📘';
  return '📄';
}

function Documents() {
  const { } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState<number | null>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterCategory, setFilterCategory] = useState<DocCategory | 'all'>('all');

  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general' as DocCategory,
    fileUrl: '',
    fileName: '',
    fileSize: undefined as number | undefined,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      const data = await getDocuments();
      setDocuments(data.documents);
      setCount(data.count);
      setLimit(data.limit);
      setHiddenCount(data.hiddenCount || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (doc?: Document) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        title: doc.title,
        description: doc.description || '',
        category: doc.category,
        fileUrl: doc.fileUrl,
        fileName: doc.fileName || '',
        fileSize: doc.fileSize,
      });
    } else {
      setEditingDoc(null);
      setFormData({
        title: '',
        description: '',
        category: 'general',
        fileUrl: '',
        fileName: '',
        fileSize: undefined,
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDoc(null);
    setError('');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsUploading(true);
    try {
      const url = await uploadFile(file);
      setFormData(prev => ({
        ...prev,
        fileUrl: url,
        fileName: file.name,
        fileSize: file.size,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'File upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!editingDoc && !formData.fileUrl) {
      setError('Please upload a file');
      return;
    }

    setIsSaving(true);
    try {
      if (editingDoc) {
        await updateDocument(editingDoc.docId, {
          title: formData.title,
          description: formData.description || undefined,
          category: formData.category,
        });
        setSuccessMessage('Document updated successfully');
      } else {
        await createDocument({
          title: formData.title,
          description: formData.description || undefined,
          category: formData.category,
          fileUrl: formData.fileUrl,
          fileName: formData.fileName || undefined,
          fileSize: formData.fileSize,
        });
        setSuccessMessage('Document added successfully');
      }

      await loadDocuments();
      handleCloseModal();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (docId: string, title: string) => {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;

    setError('');
    try {
      await deleteDocument(docId);
      setSuccessMessage('Document deleted');
      await loadDocuments();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  const filteredDocuments = filterCategory === 'all'
    ? documents
    : documents.filter(d => d.category === filterCategory);

  const atLimit = limit !== null && count >= limit;

  if (isLoading) {
    return <div className="loading">Loading documents...</div>;
  }

  return (
    <div className="documents-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="page-subtitle">Share policies, minutes, and forms with your club</p>
        </div>
        <div className="page-header-actions">
          {!atLimit && (
            <button onClick={() => handleOpenModal()} className="btn btn-primary">
              Add Document
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Usage indicator */}
      {limit !== null && (
        <div className={`doc-usage-bar ${atLimit ? 'doc-usage-at-limit' : ''}`}>
          <span className="doc-usage-text">
            {count} of {limit} documents used
          </span>
          <div className="doc-usage-track">
            <div
              className="doc-usage-fill"
              style={{ width: `${Math.min((count / limit) * 100, 100)}%` }}
            />
          </div>
          {atLimit && hiddenCount === 0 && (
            <span className="doc-usage-warning">
              Limit reached — <a href="/upgrade">upgrade your plan</a> to add more
            </span>
          )}
        </div>
      )}

      {/* Hidden-from-public banner (over-limit, eg after downgrade) */}
      {hiddenCount > 0 && limit !== null && (
        <div className="doc-usage-bar doc-usage-at-limit" style={{ marginTop: 12 }}>
          <span className="doc-usage-warning">
            👁️ {hiddenCount} {hiddenCount === 1 ? 'document is' : 'documents are'} hidden from your public site —
            your plan shows the latest {limit} on the website.{' '}
            <a href="/upgrade">Upgrade</a> to show all.
          </span>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📄</div>
          <h3>No documents yet</h3>
          <p>Share policies, minutes, and forms with your club</p>
          <button onClick={() => handleOpenModal()} className="btn btn-primary" style={{ marginTop: 16 }}>
            Add First Document
          </button>
        </div>
      ) : (
        <>
          {/* Category filter */}
          <div className="doc-filter-bar">
            <button
              className={`doc-filter-btn ${filterCategory === 'all' ? 'active' : ''}`}
              onClick={() => setFilterCategory('all')}
            >
              All ({documents.length})
            </button>
            {CATEGORIES.filter(cat => documents.some(d => d.category === cat)).map(cat => (
              <button
                key={cat}
                className={`doc-filter-btn ${filterCategory === cat ? 'active' : ''}`}
                onClick={() => setFilterCategory(cat)}
              >
                {CATEGORY_LABELS[cat]} ({documents.filter(d => d.category === cat).length})
              </button>
            ))}
          </div>

          {filteredDocuments.length === 0 ? (
            <div className="doc-empty-filter">
              <p>No documents in this category.</p>
            </div>
          ) : (
            <div className="doc-table-wrapper">
              <table className="doc-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Category</th>
                    <th>File</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map(doc => (
                    <tr key={doc.docId} className={doc.hiddenFromPublic ? 'doc-row-hidden' : ''}>
                      <td>
                        <div className="doc-title-cell">
                          <span className="doc-file-icon">{getFileIcon(doc.fileName)}</span>
                          <div>
                            <div className="doc-title">
                              {doc.title}
                              {doc.hiddenFromPublic && (
                                <span className="doc-hidden-badge" title="Not shown on your public site — upgrade to show all">
                                  👁️ Hidden from public
                                </span>
                              )}
                            </div>
                            {doc.description && (
                              <div className="doc-description">{doc.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`doc-category-badge doc-category-${doc.category}`}>
                          {CATEGORY_LABELS[doc.category]}
                        </span>
                      </td>
                      <td>
                        <div className="doc-file-info">
                          {doc.fileName && <span className="doc-file-name">{doc.fileName}</span>}
                          {doc.fileSize && (
                            <span className="doc-file-size">{formatFileSize(doc.fileSize)}</span>
                          )}
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="doc-download-link"
                          >
                            Download
                          </a>
                        </div>
                      </td>
                      <td className="doc-date">
                        {new Date(doc.createdAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td>
                        <div className="doc-actions">
                          <button
                            onClick={() => handleOpenModal(doc)}
                            className="btn btn-secondary btn-small"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(doc.docId, doc.title)}
                            className="btn btn-danger btn-small"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" {...overlayClose(handleCloseModal)}>
          <div className="modal-content doc-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingDoc ? 'Edit Document' : 'Add Document'}
              </h2>
              <button onClick={handleCloseModal} className="modal-close">&times;</button>
            </div>

            {error && <div className="error-message" style={{ margin: '0 0 16px' }}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="doc-title" className="form-label">Title *</label>
                <input
                  id="doc-title"
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Club Safeguarding Policy 2024"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="doc-category" className="form-label">Category</label>
                <select
                  id="doc-category"
                  className="form-input"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value as DocCategory })}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="doc-description" className="form-label">Description (optional)</label>
                <textarea
                  id="doc-description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Brief description of this document..."
                />
              </div>

              {/* File upload — only shown when creating */}
              {!editingDoc && (
                <div className="form-group">
                  <label className="form-label">File *</label>
                  <div
                    className={`doc-upload-area ${formData.fileUrl ? 'doc-upload-done' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="doc-upload-input"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                    {isUploading ? (
                      <div className="doc-upload-uploading">
                        <div className="doc-upload-spinner" />
                        <span>Uploading...</span>
                      </div>
                    ) : formData.fileUrl ? (
                      <div className="doc-upload-success">
                        <span className="doc-upload-checkmark">✓</span>
                        <span>{formData.fileName || 'File uploaded'}</span>
                        {formData.fileSize && (
                          <span className="doc-upload-size">{formatFileSize(formData.fileSize)}</span>
                        )}
                      </div>
                    ) : (
                      <div className="doc-upload-prompt">
                        <span className="doc-upload-icon">📎</span>
                        <span>Click to choose a file</span>
                        <span className="doc-upload-hint">PDF, DOC, DOCX</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-outline">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSaving || isUploading}
                >
                  {isSaving ? 'Saving...' : editingDoc ? 'Save Changes' : 'Add Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
